import { isAppError } from "./type-guards";

/**
 * 类型守卫：检查错误是否为 JSON Response
 */
export function isJsonResponse(error: unknown): error is Response {
  return (
    error instanceof Response &&
    error.headers.get("content-type")?.includes("application/json") === true
  );
}

/**
 * 从 JSON 对象中提取错误详情
 *
 * 支持两种格式：
 * 1. handleServerError 格式: {"error":{"message":"...","statusCode":500,"requestId":"..."}}
 * 2. TanStack Start 未处理格式: {"status":500,"unhandled":true,"message":"HTTPError"}
 */
function parseErrorJson(data: unknown): {
  message: string;
  requestId?: string;
  statusCode?: number;
} | null {
  if (!data || typeof data !== "object") {
    return null;
  }

  const obj = data as Record<string, unknown>;

  // 格式 1: handleServerError → {"error":{...}}
  if ("error" in obj && obj.error && typeof obj.error === "object") {
    const errorObj = obj.error as Record<string, unknown>;
    return {
      message: typeof errorObj.message === "string" ? errorObj.message : "An error occurred",
      requestId: typeof errorObj.requestId === "string" ? errorObj.requestId : undefined,
      statusCode: typeof errorObj.statusCode === "number" ? errorObj.statusCode : undefined,
    };
  }

  // 格式 2: TanStack Start 未处理 → {"status":500,"unhandled":true,"message":"HTTPError"}
  if (typeof obj.status === "number") {
    return {
      message: typeof obj.message === "string" ? obj.message : "An error occurred",
      statusCode: obj.status,
    };
  }

  return null;
}

/**
 * 同步提取错误的 HTTP 状态码
 *
 * 用于 retry 等不需要完整错误详情的场景，避免重复 JSON.parse 逻辑。
 * 支持 Response 对象和 Error(JSON message) 两种传入方式。
 */
export function getErrorStatusCode(error: unknown): number | undefined {
  if (error instanceof Response) {
    return error.status;
  }
  if (isAppError(error)) {
    return error.statusCode;
  }
  if (error instanceof Error) {
    try {
      return parseErrorJson(JSON.parse(error.message))?.statusCode;
    } catch {
      return;
    }
  }
}

/**
 * 从错误中提取详细信息
 *
 * 用于客户端错误处理，支持：
 * - JSON Response (服务端返回的错误，含 requestId)
 * - AppError
 * - Error (含 TanStack Start JSON message 包装)
 */
export async function extractErrorDetail(error: unknown): Promise<{
  message: string;
  requestId?: string;
  statusCode?: number;
}> {
  if (isJsonResponse(error)) {
    try {
      const data = await error.clone().json();
      const result = parseErrorJson(data);
      if (result) {
        return result;
      }
      return { message: "An error occurred" };
    } catch {
      return { message: "An error occurred" };
    }
  }

  if (isAppError(error)) {
    return { message: error.message, statusCode: error.statusCode };
  }

  if (error instanceof Error) {
    try {
      const result = parseErrorJson(JSON.parse(error.message));
      if (result) {
        return result;
      }
    } catch {
      // message 不是 JSON，正常 fallback
    }
    return { message: error.message };
  }

  return { message: "An unknown error occurred" };
}

/**
 * 从错误中提取错误信息
 *
 * 用于客户端错误处理，内部调用 extractErrorDetail
 */
export async function extractErrorMessage(error: unknown): Promise<string> {
  const { message } = await extractErrorDetail(error);
  return message;
}
